/**
 * PlayerProfileModal
 *
 * Coach-only peek at a player's basic profile (age, UTR, NTRP,
 * training goal). Opens from the player's avatar/name in:
 *  - the conversation header
 *  - their own message bubbles in the thread
 *  - the player meta strip on CoachLessonDetail
 *  - the player card on CoachBundleProgress
 *
 * Read-only fetch via `getUserProfile(playerId)` on mount. Empty fields
 * are hidden (no "Not provided" placeholders); when nothing is set we
 * show a single muted line. Portal + zIndex 9999 + ESC / backdrop close
 * mirror the ProgressReportModal pattern.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getUserProfile } from '../services/userProfile';
import type { UserProfile } from '../types/user';
import { playerLabelsFromProfile } from '../utils/parentAccountDisplay';

interface PlayerProfileModalProps {
  playerId: string;
  /** Render-time fallbacks for the header while the profile fetch is in
   *  flight or if it fails. Callers usually have these on hand from the
   *  order or message. */
  seedName?: string;
  seedAvatarUrl?: string | null;
  onClose: () => void;
}

function readGoal(profile: UserProfile | null): string | null {
  if (!profile) return null;
  // `goal` is set on the user doc + playerProfile via AccountPage but
  // isn't in the typed PlayerProfile interface — defensive read matches
  // the pattern in CoachBundleProgress / earlier PlayerProgressReport.
  const fromNested =
    (profile.playerProfile as unknown as Record<string, unknown> | undefined)?.goal as string | undefined;
  const fromTop =
    (profile as unknown as Record<string, unknown> | undefined)?.goal as string | undefined;
  const value = (fromNested ?? fromTop ?? '').trim();
  return value.length > 0 ? value : null;
}

/**
 * Resolve the player's age. For parent accounts the source of truth is
 * userProfile.playerDOB (the child's DOB, written by verifyParentWithCard
 * as a Firestore Timestamp; surfaces here as either an ISO string or
 * a `{ seconds, nanoseconds }` shape depending on serialization). For
 * regular player accounts we fall back to the typed playerProfile.age.
 */
function computePlayerAge(profile: UserProfile | null): number | null {
  if (!profile) return null;
  const playerDOB = (profile as unknown as Record<string, unknown>).playerDOB;
  if (playerDOB) {
    let date: Date | null = null;
    if (typeof playerDOB === 'string') {
      const d = new Date(playerDOB);
      if (!Number.isNaN(d.getTime())) date = d;
    } else if (typeof playerDOB === 'object' && playerDOB !== null && 'seconds' in playerDOB) {
      const seconds = (playerDOB as { seconds: number }).seconds;
      if (typeof seconds === 'number') date = new Date(seconds * 1000);
    }
    if (date) {
      const now = new Date();
      let age = now.getFullYear() - date.getFullYear();
      const m = now.getMonth() - date.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < date.getDate())) age--;
      if (age >= 0 && age < 130) return age;
    }
  }
  if (typeof profile.playerProfile?.age === 'number') {
    return profile.playerProfile.age;
  }
  return null;
}

export default function PlayerProfileModal({
  playerId,
  seedName,
  seedAvatarUrl,
  onClose,
}: PlayerProfileModalProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUserProfile(playerId)
      .then(p => { if (!cancelled) setProfile(p ?? null); })
      .catch(() => { /* fall through; header still renders from seed values */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playerId]);

  // ESC close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Body scroll lock.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, []);

  // Focus trap.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    dialog.addEventListener('keydown', onKeyDown);
    return () => dialog.removeEventListener('keydown', onKeyDown);
  }, []);

  // ── Resolve display values ──────────────────────────────────────────────────
  const player = profile?.playerProfile;
  // Parent-aware naming. For parent accounts the playing person is the youth
  // profile (child) attached to the parent's user doc; we surface the child's
  // first name as the primary label and the parent's name as a secondary tag.
  const labels = playerLabelsFromProfile(profile);
  const displayName = labels.primary || seedName || 'Player';
  const parentTag = labels.parent;
  const avatarUrl = profile?.avatarUrl ?? player?.avatarUrl ?? seedAvatarUrl ?? undefined;
  const initial = (displayName || '?').charAt(0).toUpperCase();

  const age = computePlayerAge(profile);
  const utr = typeof player?.utr === 'number' ? player.utr : null;
  const ntrp = typeof player?.ntrp === 'number' ? player.ntrp : null;
  const goal = readGoal(profile);

  // Inline rows render single-line "Label X" / "Label: X". Block rows render
  // an eyebrow + body and an optional sub-line beneath.
  type InlineRow = { kind: 'inline'; text: string };
  type BlockRow = { kind: 'block'; label: string; value: string; sublabel?: string };
  const rows: (InlineRow | BlockRow)[] = [];
  if (age != null) rows.push({ kind: 'inline', text: `Age ${age}` });
  if (utr != null) rows.push({ kind: 'inline', text: `UTR: ${utr}` });
  if (ntrp != null) rows.push({ kind: 'inline', text: `NTRP: ${ntrp}` });
  if (goal) {
    rows.push({ kind: 'block', label: 'Training goal', value: goal });
  }

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.4)', zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="player-profile-title"
    >
      <div
        ref={dialogRef}
        className="w-full max-w-[400px] bg-white rounded-[16px] overflow-hidden"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}
      >
        <div className="relative px-6 py-6">
          {/* Close X */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 p-2 rounded-full text-db-text-3 hover:text-db-text-1 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6L18 18M18 6L6 18" />
            </svg>
          </button>

          {/* Header — rectangular avatar + name */}
          <div className="flex items-center gap-4 pr-8 mb-5">
            {/* Oasis circle, the identity treatment every row that opens this
                modal already uses. It was a biscayne 4:5 rectangle at radius
                10 — the COACH CARD pattern, borrowed for a player's face, so
                tapping an identity row turned a light circle into a dark
                rectangle of the same person. */}
            <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: '#54C3EA' }}>
              {avatarUrl ? (
                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-[22px] font-bold text-white">{initial}</span>
              )}
            </div>
            <div className="min-w-0">
              <h2
                id="player-profile-title"
                className="text-[20px] md:text-[22px] font-bold text-db-text-1 leading-tight"
              >
                {displayName}
              </h2>
              {parentTag && (
                <div className="text-[12px] text-db-text-2 mt-0.5">{parentTag}</div>
              )}
            </div>
          </div>

          {/* Body — field rows or empty-state line */}
          {loading && rows.length === 0 ? (
            <div className="py-2 text-[13px] text-db-text-2">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="py-2 text-[13px] text-db-text-2">No profile info added yet.</div>
          ) : (
            <div className="flex flex-col gap-3.5">
              {rows.map((row, idx) => {
                if (row.kind === 'inline') {
                  return (
                    <div key={`inline-${idx}`} className="text-[14px] text-db-text-1 leading-snug">
                      {row.text}
                    </div>
                  );
                }
                return (
                  <div key={`block-${row.label}`}>
                    <div className="text-[11px] font-semibold uppercase tracking-[.12em] text-db-text-3 mb-1">
                      {row.label}
                    </div>
                    <div className="text-[15px] text-db-text-1 leading-snug">{row.value}</div>
                    {row.sublabel && (
                      <div className="text-[12.5px] text-db-text-2 leading-relaxed mt-0.5">
                        {row.sublabel}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
